import { extractCreatorId } from '../utils/authUtils';

// Team Member access permissions - shared between Create and Edit modals
// Analytics & Settings are admin-only (never assignable / never accessible for role `team`).
export const TEAM_ACCESS_PERMISSIONS = [
  { category: 'Dashboard', items: [
    { label: 'View', defaultOn: true }
  ]},
  { category: 'Products', items: [
    { label: 'View', defaultOn: true },
    { label: 'Update Stock', defaultOn: true },
    { label: 'Update Details', defaultOn: true },
    { label: 'Create', defaultOn: true },
    { label: 'Delete', defaultOn: true }
  ]},
  { category: 'Orders', items: [
    { label: 'View', defaultOn: true },
    { label: 'Update Status', defaultOn: true }
  ]},
  { category: 'Sales', items: [
    { label: 'View', defaultOn: true }
  ]},
  { category: 'Users', items: [
    { label: 'View', defaultOn: true },
    { label: 'Manage', defaultOn: false }
  ]},
  { category: 'Categories', items: [
    { label: 'View', defaultOn: true },
    { label: 'Manage', defaultOn: false }
  ]},
  { category: 'Revenue & Accounts', items: [
    { label: 'View', defaultOn: true },
    { label: 'Manage', defaultOn: false }
  ]},
  { category: 'My Account', items: [
    { label: 'Full Access', defaultOn: true }
  ]}
];

/** Team may never open these admin views (regardless of stale DB permissions). */
export const TEAM_MEMBER_FORBIDDEN_VIEWS = ['analytics', 'settings'];

export const getDefaultTeamAccess = () => {
  const state = {};
  TEAM_ACCESS_PERMISSIONS.forEach(p => {
    p.items.forEach(i => {
      state[`${p.category}|${i.label}`] = i.defaultOn;
    });
  });
  return state;
};

// Map view IDs to the permission key that controls access
export const VIEW_TO_PERMISSION = {
  dashboard: 'Dashboard|View',
  'pdp-views': 'Products|View',
  products: 'Products|View',
  'add-product': 'Products|Create',  // Add Product page - requires Create permission
  orders: 'Orders|View',
  analytics: 'Analytics|View',
  sales: 'Sales|View',
  users: 'Users|View',
  categories: 'Categories|View',
  revenue: 'Revenue & Accounts|View',
  settings: 'Settings|Read Only',
  account: 'My Account|Full Access'
};

/**
 * Check if team member has access to a view based on their accessPermissions.
 * Empty/missing = deny all except My Account (backend MUST return accessPermissions in login/profile).
 * @param {string} viewId - e.g. 'dashboard', 'users', 'products'
 * @param {object} accessPermissions - from user.accessPermissions
 * @param {boolean} [isTeamMember] - when true, enforces admin-only views (analytics, settings)
 * @returns {boolean}
 */
export const hasAccessToView = (viewId, accessPermissions, isTeamMember = false) => {
  if (isTeamMember && TEAM_MEMBER_FORBIDDEN_VIEWS.includes(viewId)) {
    return false;
  }
  const key = VIEW_TO_PERMISSION[viewId];
  if (!key) return false;
  // Must have accessPermissions with keys - empty = deny (only account as fallback)
  const hasData = accessPermissions && typeof accessPermissions === 'object' && Object.keys(accessPermissions).length > 0;
  if (!hasData) {
    return viewId === 'account';
  }
  if (viewId === 'sales') {
    const v = accessPermissions[key];
    if (v === false) return false;
    if (v === true) return true;
    // Rollout: missing Sales|View → allow if they can manage products
    return accessPermissions['Products|View'] === true;
  }
  return accessPermissions[key] === true;
};

/** Get first view ID the team member has access to (for redirect) */
export const getFirstAccessibleView = (accessPermissions, isTeamMember = false) => {
  const viewIds = Object.keys(VIEW_TO_PERMISSION);
  for (const id of viewIds) {
    if (hasAccessToView(id, accessPermissions, isTeamMember)) return id;
  }
  return 'account'; // My Account - always allowed as fallback
};

/**
 * Check if current admin/team user has a specific permission (for conditional UI).
 * Use with getAdminUser() - pass user or get from localStorage.
 * @param {string} permissionKey - e.g. 'Products|View', 'Products|Update Stock'
 * @param {object} [user] - admin user object; if omitted, reads from localStorage
 * @returns {boolean}
 */
export const hasAccess = (permissionKey, user) => {
  let u = user;
  if (!u && typeof window !== 'undefined') {
    try {
      const stored = localStorage.getItem('adminUser');
      u = stored ? JSON.parse(stored) : null;
    } catch { u = null; }
  }
  if (!u) return false;
  if (u.role === 'admin') return true;
  if (u.role === 'team') {
    if (permissionKey === 'Analytics|View' || permissionKey === 'Settings|Read Only') {
      return false;
    }
    const perms = u.accessPermissions;
    const hasPerms = perms && typeof perms === 'object' && Object.keys(perms).length > 0;
    if (!hasPerms) return false;
    if (permissionKey === 'Sales|View') {
      const v = perms[permissionKey];
      if (v === false) return false;
      if (v === true) return true;
      return perms['Products|View'] === true;
    }
    return perms[permissionKey] === true;
  }
  return false;
};

/** True when product.createdBy matches the logged-in admin/team user. */
export function isProductOwnedByUser(product, user) {
  if (!product || !user) return false;
  const ownerId = extractCreatorId(product.createdBy);
  const myId = String(user._id || user.id || '').trim();
  return Boolean(ownerId && myId && ownerId === myId);
}

/**
 * Whether delete UI/API should be offered for this product.
 * Team: own products only, with Products|Delete or Products|Create.
 */
export function canDeleteProduct(user, product) {
  if (!user || !product) return false;
  if (user.role === 'admin') return true;
  if (user.role !== 'team') return false;
  if (!isProductOwnedByUser(product, user)) return false;
  return (
    hasAccess('Products|Delete', user) === true || hasAccess('Products|Create', user) === true
  );
}

/** Featured catalog flag — store admin only (not team members). */
export function canSetProductFeatured(user) {
  return Boolean(user && user.role === 'admin');
}

/** Remove isFeatured from create/update payloads when caller is team. */
export function omitProductFeaturedForNonAdmin(user, payload) {
  if (!payload || typeof payload !== 'object' || canSetProductFeatured(user)) {
    return payload;
  }
  const next = { ...payload };
  delete next.isFeatured;
  return next;
}
